import React, { useState, useEffect } from 'react';
import { Table, Card, Tag, Button, Space, message, Modal, Tooltip, Row, Col, Statistic } from 'antd';
import { EyeOutlined, ReloadOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { 
    loadLoanRequestsByUserId
} from '../../redux/rtk/features/loanRequest/loanRequestSlice';
import moment from 'moment';

const LoanStatus = ({ userRole, userId }) => {
    const [loans, setLoans] = useState([]);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0
    });
    
    const dispatch = useDispatch();
    const { list: loanRequests, loading } = useSelector((state) => state.loanRequest);

    useEffect(() => {
        fetchData();
    }, [userRole, userId, dispatch]);

    const fetchData = () => {
        console.log('Fetching data for userRole:', userRole, 'userId:', userId);
        
        // Always fetch only the current user's own loan requests, regardless of role
        if (userId) {
            dispatch(loadLoanRequestsByUserId(userId));
        } else {
            console.error('User ID is missing');
        }
    };

    // Calculate statistics when loanRequests change
    useEffect(() => {
        if (loanRequests && Array.isArray(loanRequests)) {
            const total = loanRequests.length;
            const pending = loanRequests.filter(req => req.status === 'pending').length;
            const approved = loanRequests.filter(req => req.status === 'approved').length;
            const rejected = loanRequests.filter(req => req.status === 'rejected').length;

            setStats({ total, pending, approved, rejected });

            // Extract approved loans
            const approvedLoans = loanRequests
                .filter(req => req.status === 'approved' && req.loan)
                .map(req => req.loan);
            setLoans(approvedLoans);
        } else {
            // Reset stats if no data or invalid data
            setStats({ total: 0, pending: 0, approved: 0, rejected: 0 });
            setLoans([]);
        }
    }, [loanRequests]);

    const getStatusTag = (status) => {
        const statusConfig = {
            pending: { color: 'orange', text: 'Pending', icon: <ClockCircleOutlined /> },
            approved: { color: 'green', text: 'Approved', icon: <CheckCircleOutlined /> },
            rejected: { color: 'red', text: 'Rejected', icon: <CloseCircleOutlined /> },
        };
        
        const config = statusConfig[status] || { color: 'default', text: status, icon: null };
        return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
    };

    const requestColumns = [
        {
            title: 'Employee',
            dataIndex: 'user',
            key: 'user',
            render: (user) => user ? `${user.firstName} ${user.lastName}` : 'N/A',
        },
        {
            title: 'Amount (£)',
            dataIndex: 'requestedAmount',
            key: 'requestedAmount',
            render: (amount) => `£${amount?.toLocaleString()}`,
        },
        {
            title: 'Type',
            dataIndex: 'loanType',
            key: 'loanType',
            render: (loanType) => {
                if (loanType === null) {
                    return <span>--</span>;
                } else if (loanType === false || loanType === 0) {
                    return <Tag color="blue">Loan</Tag>;
                } else if (loanType === true || loanType === 1) {
                    return <Tag color="green">Advance Payment</Tag>;
                }
                return <span>--</span>; // Default fallback for unexpected values
            },
        },
        {
            title: 'Purpose',
            dataIndex: 'purpose',
            key: 'purpose',
            ellipsis: true,
            render: (text) => (
                <Tooltip title={text}>
                    <span>{text}</span>
                </Tooltip>
            ),
        },
        {
            title: 'Returning Months',
            dataIndex: 'returningMonths',
            key: 'returningMonths',
            render: (months) => months ? `${months} months` : 'N/A',
        },
        {
            title: 'Requested Date',
            dataIndex: 'requestedDate',
            key: 'requestedDate',
            render: (date) => moment(date).format('DD/MM/YYYY'),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => getStatusTag(status),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => setSelectedRequest(record)}
                    >
                        View Details
                    </Button>
                </Space>
            ),
        },
    ];

    const loanColumns = [
        // {
        //     title: 'Loan ID',
        //     dataIndex: 'id',
        //     key: 'id',
        // },
        {
            title: 'Amount (£)',
            dataIndex: 'loanAmount',
            key: 'loanAmount',
            render: (amount) => `£${amount?.toLocaleString()}`,
        },
        {
            title: 'Returning Months',
            dataIndex: 'totalMonthReturn',
            key: 'totalMonthReturn',
            render: (months) => months ? `${months} months` : 'N/A',
        },
        {
            title: 'Start Date',
            dataIndex: 'startDate',
            key: 'startDate',
            render: (date) => moment(date).format('DD/MM/YYYY'),
        },
        {
            title: 'Balance (£)',
            dataIndex: 'balance',
            key: 'balance',
            render: (balance) => `£${balance?.toLocaleString()}`,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={status ? 'green' : 'red'}>
                    {status ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
    ];

    return (
        <div>
            {/* Statistics Cards */}
            <Row gutter={16} style={{ marginBottom: '20px' }}>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="My Total Requests"
                            value={stats.total}
                            prefix={<ClockCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="My Pending"
                            value={stats.pending}
                            valueStyle={{ color: '#fa8c16' }}
                            prefix={<ClockCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="My Approved"
                            value={stats.approved}
                            valueStyle={{ color: '#52c41a' }}
                            prefix={<CheckCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="My Rejected"
                            value={stats.rejected}
                            valueStyle={{ color: '#f5222d' }}
                            prefix={<CloseCircleOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Loan Requests Table */}
            <Card 
                title="My Advance Requests" 
                extra={
                    <Button 
                        icon={<ReloadOutlined />} 
                        onClick={fetchData}
                        loading={loading}
                    >
                        Refresh
                    </Button>
                }
                style={{ marginBottom: '20px' }}
            >
                <Table
                    columns={requestColumns}
                    dataSource={Array.isArray(loanRequests) ? loanRequests : []}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    }}
                />
            </Card>

            {/* Active Loans Table (only show if there are approved loans) */}
            {Array.isArray(loans) && loans.length > 0 && (
                <Card title="My Active Advance">
                    <Table
                        columns={loanColumns}
                        dataSource={loans}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        }}
                    />
                </Card>
            )}

            {/* Request Details Modal */}
            <Modal
                title="Loan Request Details"
                open={!!selectedRequest}
                onCancel={() => setSelectedRequest(null)}
                footer={[
                    <Button key="close" onClick={() => setSelectedRequest(null)}>
                        Close
                    </Button>
                ]}
                width={600}
            >
                {selectedRequest && (
                    <div>
                        <p><strong>Employee:</strong> {selectedRequest.user?.firstName} {selectedRequest.user?.lastName}</p>
                        <p><strong>Amount:</strong> £{selectedRequest.requestedAmount?.toLocaleString()}</p>
                        <p><strong>Purpose:</strong> {selectedRequest.purpose}</p>
                        <p><strong>Returning Months:</strong> {selectedRequest.returningMonths} months</p>
                        <p><strong>Requested Date:</strong> {moment(selectedRequest.requestedDate).format('DD/MM/YYYY HH:mm')}</p>
                        <p><strong>Status:</strong> {getStatusTag(selectedRequest.status)}</p>
                        {selectedRequest.rejectionReason && (
                            <p><strong>Rejection Reason:</strong> {selectedRequest.rejectionReason}</p>
                        )}
                        {selectedRequest.loan && (
                            <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f6f8fa', borderRadius: '6px' }}>
                                <h4>Approved Loan Details:</h4>
                                <p><strong>Loan ID:</strong> {selectedRequest.loan.id}</p>
                                <p><strong>Loan Amount:</strong> £{selectedRequest.loan.loanAmount?.toLocaleString()}</p>
                                <p><strong>Returning Months:</strong> {selectedRequest.loan.totalMonthReturn} months</p>
                                <p><strong>Start Date:</strong> {moment(selectedRequest.loan.startDate).format('DD/MM/YYYY')}</p>
                                <p><strong>Balance:</strong> £{selectedRequest.loan.balance?.toLocaleString()}</p>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default LoanStatus;
